// components/EnhancedReactionBar.js — Bold modern redesign
// Clean threaded replies · flat reply rows · DM Sans · 8px radius system
// No bubble shapes, no heavy left borders, no inconsistent fonts

import React, { useState } from "react";
import {
  Box, Typography, Menu, Avatar,
  Button, TextField, CircularProgress, Collapse, Tooltip,
} from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import {
  ThumbUp              as LikeIcon,
  Favorite             as LoveIcon,
  EmojiEmotions        as LaughIcon,
  SentimentSatisfied   as WowIcon,
  SentimentDissatisfied      as SadIcon,
  SentimentVeryDissatisfied  as AngryIcon,
  Visibility           as VisibilityIcon,
  ChatBubbleOutline    as CommentIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import API              from "../../../../../API";
import { useCreatorAuth } from "../../../../../Hooks/creator-use-auth";
import useAuth          from "../../../../../Hooks/use-auth";
import { getCleanDisplayName, getReplyDisplayName } from "./activityHelpers";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────
   Design tokens — keep in sync with ZapFeed.js / FeedItem.js
───────────────────────────────────────────────────────── */
const FONT = {
  display: "'Bricolage Grotesque', sans-serif",
  body:    "'DM Sans', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   Reaction config
───────────────────────────────────────────────────────── */
const REACTIONS = {
  like:  { icon: LikeIcon,  label: "Like",  color: "#2563eb", emoji: "👍" },
  love:  { icon: LoveIcon,  label: "Love",  color: "#e91e63", emoji: "❤️" },
  laugh: { icon: LaughIcon, label: "Haha",  color: "#d97706", emoji: "😂" },
  wow:   { icon: WowIcon,   label: "Wow",   color: "#7c3aed", emoji: "😮" },
  sad:   { icon: SadIcon,   label: "Sad",   color: "#0891b2", emoji: "😢" },
  angry: { icon: AngryIcon, label: "Angry", color: "#ef4444", emoji: "😠" },
};

/* ─────────────────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────────────────── */
const popIn = keyframes`
  0%   { transform: scale(0.72) translateY(4px); opacity: 0; }
  65%  { transform: scale(1.05) translateY(-1px); }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
function invalidateAllFeeds(queryClient) {
  queryClient.invalidateQueries(["zapfeed4-modular"]);
  queryClient.invalidateQueries({ queryKey: ["creator.zapfeed"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["bubble-feed"],     exact: false });
  queryClient.invalidateQueries({ queryKey: ["post-replies"],    exact: false });
}

const fmt = (n) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(1)}M`
  : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K`
  : String(n || 0);

/* ─────────────────────────────────────────────────────────
   ReactionSummary — emoji stack + counts, compact
───────────────────────────────────────────────────────── */
function ReactionSummary({ reactionCounts, totalReactions, replyCount, onRepliesClick }) {
  const topReacts = Object.entries(reactionCounts)
    .filter(([, c]) => c > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  if (!topReacts.length && !replyCount) return null;

  return (
    <Box
      display="flex" alignItems="center" justifyContent="space-between"
      sx={{ mb: 0.625, animation: `${fadeIn} 0.18s ease` }}
    >
      {/* Left: emoji stack + count */}
      <Box display="flex" alignItems="center" gap={0.625}>
        {topReacts.length > 0 && (
          <>
            <Box sx={{ display: "flex", "& > *:not(:first-of-type)": { ml: "-4px" } }}>
              {topReacts.map(([type]) => (
                <Box key={type} sx={{
                  width: 18, height: 18, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: (t) => `1.5px solid ${t.palette.background.paper}`,
                  fontSize: "10px", lineHeight: 1,
                }}>
                  {REACTIONS[type]?.emoji}
                </Box>
              ))}
            </Box>
            <Typography sx={{
              fontSize: "0.75rem", fontWeight: 500,
              color: "text.disabled", fontFamily: FONT.body,
            }}>
              {fmt(totalReactions)}
            </Typography>
          </>
        )}
      </Box>

      {/* Right: reply count */}
      {replyCount > 0 && (
        <Typography
          onClick={onRepliesClick}
          sx={{
            fontSize: "0.75rem", fontWeight: 500,
            color: "text.disabled", fontFamily: FONT.body,
            cursor: "pointer",
            "&:hover": { color: "text.secondary" },
            transition: "color 0.12s",
          }}
        >
          {fmt(replyCount)} {replyCount === 1 ? "reply" : "replies"}
        </Typography>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   ActionButton — clean text+icon button, no pill shape
───────────────────────────────────────────────────────── */
function ActionButton({ onClick, active, activeColor, icon, count, rightAlign, disabled }) {
  const theme = useTheme();
  const color = active ? activeColor : theme.palette.text.disabled;

  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        px: 0.875, py: 0.5, borderRadius: "8px",
        cursor: disabled ? "default" : "pointer",
        userSelect: "none",
        color,
        transition: "background 0.12s, color 0.12s",
        ml: rightAlign ? "auto" : 0,
        "&:hover": disabled ? {} : {
          bgcolor: alpha(activeColor || theme.palette.text.secondary, 0.07),
          color: activeColor || theme.palette.text.primary,
          "& .action-icon": { transform: "scale(1.1)" },
        },
        "&:active": disabled ? {} : { transform: "scale(0.95)" },
      }}
    >
      <Box className="action-icon" sx={{ display: "flex", lineHeight: 1, transition: "transform 0.12s" }}>
        {icon}
      </Box>
      {count != null && count > 0 && (
        <Typography sx={{
          fontSize: "0.78rem", fontWeight: 500, lineHeight: 1,
          fontFamily: FONT.body, color: "inherit",
        }}>
          {fmt(count)}
        </Typography>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   ReplyItem — flat Twitter-style reply row (no bubble)
───────────────────────────────────────────────────────── */
function ReplyItem({ reply, isLast }) {
  const theme    = useTheme();
  const navigate = useNavigate();

  const displayName = getReplyDisplayName(reply);
  const avatar      = reply.author?.pfphref || reply.pfphref;
  const text        = reply.text || reply.activity_name || "";
  const timestamp   = reply.created_at
    ? new Date(reply.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "just now";

  const getRoute = () => {
    if (reply.is_private || displayName === "Anonymous User") return null;
    if (reply.author?.type === "creator" || reply.creator_id) {
      const id = reply.creator_id || reply.author?.username || reply.author?.id;
      return id ? `/profile/${id}` : null;
    }
    if (reply.author?.type === "normal" || reply.user_id) {
      const id = reply.user_id || reply.author?.user_id;
      return id ? `/profile/${id}` : null;
    }
    return null;
  };

  const route = getRoute();

  return (
    <Box sx={{
      display: "flex", gap: 1.25,
      py: 1.125,
      borderBottom: isLast ? "none" : `1px solid ${alpha(theme.palette.divider, 0.35)}`,
      animation: `${slideUp} 0.18s ease both`,
    }}>
      {/* Avatar */}
      <Avatar
        src={avatar}
        alt={displayName}
        sx={{
          width: 26, height: 26, flexShrink: 0, mt: 0.15,
          cursor: route ? "pointer" : "default",
          fontSize: "0.65rem",
          "&:hover": route ? { opacity: 0.75 } : {},
          transition: "opacity 0.12s",
        }}
        onClick={route ? (e) => { e.stopPropagation(); navigate(route); } : undefined}
      >
        {displayName[0]}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name + time inline */}
        <Box display="flex" alignItems="baseline" gap={0.625} mb={0.25}>
          <Typography
            sx={{
              fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.2,
              fontFamily: FONT.body, color: "text.primary",
              letterSpacing: "-0.01em",
              cursor: route ? "pointer" : "default",
              "&:hover": route ? { textDecoration: "underline" } : {},
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 130,
            }}
            onClick={route ? (e) => { e.stopPropagation(); navigate(route); } : undefined}
          >
            {displayName}
          </Typography>
          <Typography sx={{
            fontSize: "0.7rem", color: "text.disabled",
            fontFamily: FONT.body, flexShrink: 0, fontWeight: 400,
          }}>
            {timestamp}
          </Typography>
        </Box>

        {/* Reply text */}
        <Typography sx={{
          fontSize: "0.845rem", lineHeight: 1.5,
          color: "text.secondary", fontFamily: FONT.body,
          wordBreak: "break-word",
        }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   ReplyComposer — inline, matches main Composer style
───────────────────────────────────────────────────────── */
function ReplyComposer({ activity, onCancel, onSubmit, isSubmitting }) {
  const theme    = useTheme();
  const [text, setText] = useState("");

  const { creatorToken } = useCreatorAuth();
  const { isAuthorized: userOK, getCurrrentUser } = useAuth();
  const isCreator = !!creatorToken?.creator_user;

  let photo;
  if (isCreator) {
    const c = creatorToken.creator_user;
    photo = c.creator?.pfphref || c.pfphref;
  } else if (userOK) {
    photo = getCurrrentUser()?.phphref;
  }

  const remaining = 280 - text.length;
  const canSend   = text.trim() && remaining >= 0;

  return (
    <Box sx={{
      mt: 1.25, pt: 1.25,
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      animation: `${slideUp} 0.16s ease`,
    }}>
      {/* "Replying to" label */}
      <Typography sx={{
        fontSize: "0.72rem", color: "text.disabled",
        fontFamily: FONT.body, mb: 0.875, display: "block",
      }}>
        Replying to{" "}
        <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {getCleanDisplayName(activity)}
        </Box>
      </Typography>

      <Box display="flex" gap={1.25} alignItems="flex-start">
        <Avatar src={photo} sx={{ width: 30, height: 30, flexShrink: 0, mt: 0.2 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            multiline minRows={2} maxRows={6} fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSend) {
                e.preventDefault();
                onSubmit(text.trim());
              }
            }}
            placeholder="Write a reply…"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: "0.9rem", fontFamily: FONT.body, lineHeight: 1.5,
                "& textarea::placeholder": {
                  color: alpha(theme.palette.text.secondary, 0.5), opacity: 1,
                  fontFamily: FONT.body,
                },
              },
            }}
          />

          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75} mt={1}>
            {/* Char count — only show when near limit */}
            {remaining < 60 && (
              <Typography sx={{
                fontSize: "0.72rem", fontFamily: FONT.body, fontWeight: 500,
                color: remaining < 0 ? "error.main" : remaining < 20 ? "warning.main" : "text.disabled",
                mr: "auto",
              }}>
                {remaining}
              </Typography>
            )}

            <Button
              size="small" onClick={onCancel} disabled={isSubmitting}
              sx={{
                textTransform: "none", fontWeight: 500, fontSize: "0.8rem",
                color: "text.secondary", fontFamily: FONT.body,
                borderRadius: "8px", minWidth: 0, px: 1.5,
                "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.6), color: "text.primary" },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small" variant="contained"
              onClick={() => { if (canSend) onSubmit(text.trim()); }}
              disabled={!canSend || isSubmitting}
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.8rem",
                fontFamily: FONT.body, borderRadius: "8px", px: 2, minHeight: 30,
                boxShadow: "none", "&:hover": { boxShadow: "none", opacity: 0.88 },
              }}
            >
              {isSubmitting ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : "Reply"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   EmojiPicker — clean floating card
───────────────────────────────────────────────────────── */
function EmojiPicker({ anchorEl, open, onClose, onSelect, userReaction }) {
  const theme = useTheme();
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          overflow: "visible", p: "6px 4px", mt: -0.5,
        },
      }}
    >
      <Box display="flex" gap={0.25} px={0.25}>
        {Object.entries(REACTIONS).map(([type, r], i) => (
          <Tooltip key={type} title={r.label} arrow placement="top">
            <Box
              onClick={() => { onSelect(type); onClose(); }}
              sx={{
                width: 40, height: 40,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", borderRadius: "8px",
                fontSize: "20px", lineHeight: 1,
                bgcolor: userReaction === type ? alpha(r.color, 0.1) : "transparent",
                transition: "background 0.12s",
                animation: `${popIn} 0.22s cubic-bezier(0.34,1.56,0.64,1) both`,
                animationDelay: `${i * 0.025}s`,
                "&:hover": {
                  bgcolor: alpha(r.color, 0.12),
                  "& .emoji": { transform: "scale(1.28) translateY(-2px)" },
                },
              }}
            >
              <Box className="emoji" sx={{ transition: "transform 0.15s cubic-bezier(0.34,1.56,0.64,1)" }}>
                {r.emoji}
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Menu>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
export default function EnhancedReactionBar({
  activity,
  onReactionChange,
  userAuth,
  queryClient,
  onSnackbarMessage,
}) {
  const theme = useTheme();

  const [emojiAnchor,       setEmojiAnchor]       = useState(null);
  const [showReplies,       setShowReplies]        = useState(false);
  const [showComposer,      setShowComposer]       = useState(false);
  const [isSubmittingReply, setIsSubmittingReply]  = useState(false);

  const { userOK, creatorOK } = userAuth;
  const {
    activity_id,
    activity_type,
    reaction_counts = {},
    total_reactions = 0,
    user_reaction,
    can_react = false,
    view_count = 0,
    reply_count = 0,
  } = activity;

  if (!["Post", "Achievement"].includes(activity_type) || !can_react) return null;

  const curReaction = user_reaction ? REACTIONS[user_reaction] : null;

  /* ── Replies query ─────────────────────────────────── */
  const { data: repliesData, isLoading: repliesLoading } = useQuery(
    ["post-replies", activity_id],
    () => API.zapfeed.getReplies(activity_id),
    { enabled: showReplies && reply_count > 0, staleTime: 300_000 }
  );

  /* ── Optimistic patch helper ───────────────────────── */
  const patch = (updateFn) => {
    queryClient.setQueryData(["zapfeed4-modular"], (old) => {
      if (!old) return old;
      return { ...old, pages: old.pages.map((p) => ({ ...p, zapfeed: p.zapfeed.map(updateFn) })) };
    });
    queryClient.getQueryCache()
      .findAll({ queryKey: ["bubble-feed"] })
      .forEach((q) => {
        queryClient.setQueryData(q.queryKey, (old) => {
          if (!old) return old;
          return { ...old, pages: old.pages.map((p) => ({ ...p, posts: p.posts.map(updateFn) })) };
        });
      });
  };

  /* ── Add reaction ──────────────────────────────────── */
  const addReaction = useMutation(
    ({ reactionType }) => API.zapfeed.addReaction(activity_id, reactionType),
    {
      onMutate: async ({ reactionType }) => {
        await queryClient.cancelQueries(["zapfeed4-modular"]);
        await queryClient.cancelQueries({ queryKey: ["bubble-feed"], exact: false });
        const previousZapFeed = queryClient.getQueryData(["zapfeed4-modular"]);
        const previousBubbleFeeds = [];
        queryClient.getQueryCache().findAll({ queryKey: ["bubble-feed"] }).forEach((q) => {
          previousBubbleFeeds.push({ key: q.queryKey, data: q.state.data });
        });
        patch((act) => {
          if (act.activity_id !== activity_id) return act;
          const c = { ...act.reaction_counts };
          if (act.user_reaction) c[act.user_reaction] = Math.max(0, (c[act.user_reaction] || 1) - 1);
          c[reactionType] = (c[reactionType] || 0) + 1;
          return { ...act, reaction_counts: c, total_reactions: Object.values(c).reduce((s, v) => s + v, 0), user_reaction: reactionType };
        });
        return { previousZapFeed, previousBubbleFeeds };
      },
      onSuccess: (data) => { invalidateAllFeeds(queryClient); onReactionChange?.(data); },
      onError: (err, vars, ctx) => {
        if (ctx?.previousZapFeed) queryClient.setQueryData(["zapfeed4-modular"], ctx.previousZapFeed);
        ctx?.previousBubbleFeeds?.forEach(({ key, data }) => queryClient.setQueryData(key, data));
        onSnackbarMessage?.("Failed to add reaction", "error");
      },
    }
  );

  /* ── Remove reaction ───────────────────────────────── */
  const removeReaction = useMutation(
    () => API.zapfeed.removeReaction(activity_id),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(["zapfeed4-modular"]);
        await queryClient.cancelQueries({ queryKey: ["bubble-feed"], exact: false });
        const previousZapFeed = queryClient.getQueryData(["zapfeed4-modular"]);
        const previousBubbleFeeds = [];
        queryClient.getQueryCache().findAll({ queryKey: ["bubble-feed"] }).forEach((q) => {
          previousBubbleFeeds.push({ key: q.queryKey, data: q.state.data });
        });
        patch((act) => {
          if (act.activity_id !== activity_id || !act.user_reaction) return act;
          const c = { ...act.reaction_counts };
          c[act.user_reaction] = Math.max(0, (c[act.user_reaction] || 1) - 1);
          return { ...act, reaction_counts: c, total_reactions: Math.max(0, (act.total_reactions || 1) - 1), user_reaction: null };
        });
        return { previousZapFeed, previousBubbleFeeds };
      },
      onSuccess: (data) => { invalidateAllFeeds(queryClient); onReactionChange?.(data); },
      onError: (err, vars, ctx) => {
        if (ctx?.previousZapFeed) queryClient.setQueryData(["zapfeed4-modular"], ctx.previousZapFeed);
        ctx?.previousBubbleFeeds?.forEach(({ key, data }) => queryClient.setQueryData(key, data));
        onSnackbarMessage?.("Failed to remove reaction", "error");
      },
    }
  );

  /* ── Create reply ──────────────────────────────────── */
  const createReply = useMutation(
    (text) => API.zapfeed.createReply(activity_id, text),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(["zapfeed4-modular"]);
        await queryClient.cancelQueries({ queryKey: ["bubble-feed"], exact: false });
        const previousZapFeed = queryClient.getQueryData(["zapfeed4-modular"]);
        const previousBubbleFeeds = [];
        queryClient.getQueryCache().findAll({ queryKey: ["bubble-feed"] }).forEach((q) => {
          previousBubbleFeeds.push({ key: q.queryKey, data: q.state.data });
        });
        patch((item) =>
          item.activity_id === activity_id
            ? { ...item, reply_count: (item.reply_count || 0) + 1 }
            : item
        );
        return { previousZapFeed, previousBubbleFeeds };
      },
      onSuccess: () => {
        invalidateAllFeeds(queryClient);
        setShowComposer(false);
        setIsSubmittingReply(false);
        setShowReplies(true);
        onSnackbarMessage?.("Reply posted!", "success");
      },
      onError: (err, vars, ctx) => {
        if (ctx?.previousZapFeed) queryClient.setQueryData(["zapfeed4-modular"], ctx.previousZapFeed);
        ctx?.previousBubbleFeeds?.forEach(({ key, data }) => queryClient.setQueryData(key, data));
        setIsSubmittingReply(false);
        onSnackbarMessage?.("Failed to post reply", "error");
      },
    }
  );

  /* ── Handlers ──────────────────────────────────────── */
  const handleReactSelect = (type) => {
    if (user_reaction === type) removeReaction.mutate();
    else addReaction.mutate({ reactionType: type });
  };

  const handleMainReactClick = (e) => {
    e.stopPropagation();
    if (user_reaction) removeReaction.mutate();
    else setEmojiAnchor(e.currentTarget);
  };

  const isBusy = addReaction.isLoading || removeReaction.isLoading;

  const replies = repliesData?.replies ?? [];

  /* ── Render ────────────────────────────────────────── */
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ pt: 0.25 }}>

      {/* Reaction summary — only when there are reactions or replies */}
      <ReactionSummary
        reactionCounts={reaction_counts}
        totalReactions={total_reactions}
        replyCount={reply_count}
        onRepliesClick={() => setShowReplies((v) => !v)}
      />

      {/* Divider */}
      <Box sx={{ height: 1, bgcolor: alpha(theme.palette.divider, 0.45), mb: 0.25 }} />

      {/* ── Action row ──────────────────────────────── */}
      <Box display="flex" alignItems="center" sx={{ mx: -0.875, gap: 0 }}>

        {/* Comment */}
        <ActionButton
          onClick={() => setShowComposer((v) => !v)}
          active={showComposer}
          activeColor="#2563eb"
          count={reply_count}
          icon={<CommentIcon sx={{ fontSize: 16 }} />}
        />

        {/* React */}
        <ActionButton
          onClick={handleMainReactClick}
          active={!!user_reaction}
          activeColor={curReaction?.color ?? "#e91e63"}
          count={total_reactions}
          disabled={isBusy}
          icon={
            curReaction
              ? <Box sx={{ fontSize: 16, lineHeight: 1 }}>{curReaction.emoji}</Box>
              : <LikeIcon sx={{ fontSize: 16 }} />
          }
        />

        {/* Views — right-aligned, non-interactive */}
        {view_count > 0 && (
          <ActionButton
            activeColor="#6b7280"
            count={view_count}
            rightAlign
            disabled
            icon={<VisibilityIcon sx={{ fontSize: 15 }} />}
          />
        )}
      </Box>

      {/* ── Emoji picker ─────────────────────────────── */}
      <EmojiPicker
        anchorEl={emojiAnchor}
        open={Boolean(emojiAnchor)}
        onClose={() => setEmojiAnchor(null)}
        onSelect={handleReactSelect}
        userReaction={user_reaction}
      />

      {/* ── Reply composer ───────────────────────────── */}
      <Collapse in={showComposer} unmountOnExit>
        <ReplyComposer
          activity={activity}
          onCancel={() => setShowComposer(false)}
          onSubmit={(text) => { setIsSubmittingReply(true); createReply.mutate(text); }}
          isSubmitting={isSubmittingReply}
        />
      </Collapse>

      {/* ── "View N replies" link — only when collapsed ── */}
      {reply_count > 0 && !showReplies && !showComposer && (
        <Box
          onClick={() => setShowReplies(true)}
          sx={{
            display: "inline-flex", alignItems: "center",
            mt: 0.625, cursor: "pointer",
            fontSize: "0.78rem", fontFamily: FONT.body,
            fontWeight: 500, color: "text.disabled",
            "&:hover": { color: "text.secondary" },
            transition: "color 0.12s",
          }}
        >
          View {reply_count} {reply_count === 1 ? "reply" : "replies"}
        </Box>
      )}

      {/* ── Reply thread ─────────────────────────────── */}
      <Collapse in={showReplies} unmountOnExit>
        <Box sx={{
          mt: 0.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          animation: `${slideUp} 0.18s ease`,
        }}>
          {repliesLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={16} thickness={3} />
            </Box>
          ) : replies.length > 0 ? (
            <>
              {replies.map((r, idx) => (
                <ReplyItem
                  key={r.id || r.activity_id || idx}
                  reply={r}
                  isLast={idx === replies.length - 1}
                />
              ))}
              <Box
                onClick={() => setShowReplies(false)}
                sx={{
                  display: "inline-flex", alignItems: "center",
                  mt: 0.75, mb: 0.25, cursor: "pointer",
                  fontSize: "0.75rem", fontFamily: FONT.body,
                  fontWeight: 500, color: "text.disabled",
                  "&:hover": { color: "text.secondary" },
                  transition: "color 0.12s",
                }}
              >
                Hide replies
              </Box>
            </>
          ) : (
            <Typography sx={{
              py: 2, textAlign: "center", color: "text.disabled",
              fontSize: "0.82rem", fontFamily: FONT.body,
            }}>
              No replies yet
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}